/**
 * Text content policy: detects and masks card numbers and US SSNs in user messages, before a
 * message goes to the bot or a live agent. See .claude/context/message-history.md.
 */

export const REDACTION_ERROR_MESSAGE = [
  'Your submission contains restricted data (like a credit card or SSN).',
  'For your security, this cannot be processed.',
  'Please remove all sensitive info and try again.',
].join(' ');

// Matches 13-19 digit sequences with optional single space/hyphen separators;
// word-boundary anchors avoid matching inside longer digit runs.
const CARD_REGEX = /\b(?:\d[ -]?){12,18}\d\b/g;

// Matches SSN forms 123-45-6789 and 123 45 6789. The unhyphenated 9-digit
// form is intentionally omitted (too noisy / overlaps with card detection).
const SSN_REGEX = /\b\d{3}[- ]\d{2}[- ]\d{4}\b/g;

/**
 * Standard Luhn (mod-10) check used by major card networks.
 * @param {string} digits - String containing only digit characters.
 * @returns {boolean} true if the digits pass the Luhn checksum.
 */
const passesLuhn = (digits) => {
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits.charAt(i));
    if (shouldDouble) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    shouldDouble = !shouldDouble;
  }
  return sum > 0 && sum % 10 === 0;
};

/**
 * Replace every digit in `match` with `X`, preserving spaces/hyphens.
 */
const maskDigits = (match) => match.replace(/\d/g, 'X');

/**
 * Mask with `X` the digits of card numbers (13-19 digits, Luhn-valid) and SSNs in `message`.
 * Returns the masked text, whether anything matched, and the user-facing error if it did.
 *
 * @param {string} message
 * @returns {{ redacted: string, hasSensitive: boolean, error: string }}
 */
export const redactSensitiveContent = (message) => {
  if (typeof message !== 'string' || message.length === 0) {
    return { redacted: message ?? '', hasSensitive: false, error: '' };
  }

  let hasSensitive = false;

  // Card numbers — only redact if Luhn passes to limit false positives.
  let redacted = message.replace(CARD_REGEX, (match) => {
    const digits = match.replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return match;
    if (!passesLuhn(digits)) return match;
    hasSensitive = true;
    return maskDigits(match);
  });

  // SSNs — pure regex match.
  redacted = redacted.replace(SSN_REGEX, (match) => {
    hasSensitive = true;
    return maskDigits(match);
  });

  return {
    redacted,
    hasSensitive,
    error: hasSensitive ? REDACTION_ERROR_MESSAGE : '',
  };
};
